import { getProduct } from '@commerce/api/operations'
import { Product } from '@commerce/types'
import { ProductMetafields } from '@commerce/types/product'
import WishlistButton from '@components/arkade/list/WishlistButton'
import {
  FancyArrowDown,
  FancyArrowLeft,
  FancyArrowRight,
  FancyArrowUp,
} from '@components/icons'
import QuickAddButton from '@components/product/QuickAddButton'
import ColorIdentifier from '@components/ui/ColorIdentifier'
import {
  COLOUR_CARD_METAFIELD_KEY,
  COLOUR_PRODUCT_SIMILAR_COLOURS,
  findMetafieldValueByKey,
  PRODUCT_TYPE_COLOUR,
} from '@lib/helpers/general'
import cn from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
interface Props {
  slice: any
  context: any
}
const SimilarColours = ({ slice, context }: Props) => {
  const { locale } = useRouter()
  const title = slice?.primary?.title
  const similarColourProductSlugs =
    context?.metafields?.filter(
      (metafield: ProductMetafields) =>
        metafield.key === COLOUR_PRODUCT_SIMILAR_COLOURS
    ).length > 0
      ? JSON.parse(
          context?.metafields?.filter(
            (metafield: ProductMetafields) =>
              metafield.key === COLOUR_PRODUCT_SIMILAR_COLOURS
          )[0].value
        )
      : []
  const [similarColourProducts, setSimilarColourProducts] = useState<
    Product.Product[]
  >([])
  const [defaultExpandColourIndex, setDefaultExpandColourIndex] = useState(
    similarColourProductSlugs.indexOf(context.slug) === -1
      ? 0
      : similarColourProductSlugs.indexOf(context.slug)
  )
  const [selectedIndex, setSelectedIndex] = useState(defaultExpandColourIndex)
  const handleSelectLighter = () => {
    if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1)
  }
  const handleSelectDarker = () => {
    if (selectedIndex < similarColourProductSlugs.length - 1)
      setSelectedIndex(selectedIndex + 1)
  }
  useEffect(() => {
    let products: Product.Product[] = []
    const fetchSimilarColourProducts = async () => {
      const fetchProduct = similarColourProductSlugs?.map(
        async (v: string, index: number) => {
          await getProduct(locale, v, 'no-cache').then(
            (res) => (products[index] = res.product)
          )
        }
      )
      await Promise.all(fetchProduct)
      products = products.filter((product: Product.Product) => {
        return product !== undefined
      })
      setSimilarColourProducts(products)
    }
    fetchSimilarColourProducts()
  }, [])
  if (
    context?.productType !== PRODUCT_TYPE_COLOUR ||
    similarColourProductSlugs.length === 0
  )
    return <></>
  return (
    <>
      <div className="wrapper w-full bg-brand-light-grey px-16 py-20">
        <div className="">
          {title && (
            <div className="lg:heading-1 heading-mobile-1 pb-8 lg:pb-12">
              {title}
            </div>
          )}
          <div className="gallery-wrap flex h-124 w-full flex-col lg:h-162 lg:flex-row">
            {similarColourProducts &&
              similarColourProducts.map(
                (product: Product.Product, index: number) => {
                  const color = {
                    colorName: product?.name!,
                    colorHexCode: product?.colourCode!,
                  }
                  const parentProduct = {
                    handle: product?.handle!,
                    type: product?.productType!,
                    colourCode: product?.colourCode!,
                  }
                  const colourCardHandle = findMetafieldValueByKey(
                    product?.metafields!,
                    COLOUR_CARD_METAFIELD_KEY
                  )
                  const isCurrentProduct = product.slug === context.slug
                  return (
                    <div
                      onClick={() => setSelectedIndex(index)}
                      style={
                        index === selectedIndex
                          ? {
                              backgroundImage: `url(${product?.images?.[0].url})`,
                            }
                          : { backgroundColor: `${product?.colourCode!}` }
                      }
                      key={product.id}
                      className={cn('item', {
                        active: index === selectedIndex,
                      })}
                    >
                      <div
                        className={cn(
                          'absolute top-2 right-2 lg:top-10 lg:right-8',
                          {
                            hidden: index !== selectedIndex,
                          }
                        )}
                      >
                        <QuickAddButton
                          hide={false}
                          quickAddProductHandle={colourCardHandle!}
                          parentProduct={parentProduct}
                        />
                      </div>
                      <div
                        className={cn(
                          'absolute top-12 right-2 lg:top-20 lg:right-8',
                          {
                            hidden: index !== selectedIndex,
                          }
                        )}
                      >
                        <WishlistButton product={product} />
                      </div>

                      <ColorIdentifier
                        className={cn(
                          'absolute top-8  hidden flex-col lg:flex ',
                          {
                            'left-11 ': selectedIndex === index,
                          },
                          {
                            'left-0 right-0 mx-auto ': selectedIndex !== index,
                          }
                        )}
                        color={color}
                        vertialText
                      />
                      <ColorIdentifier
                        className={cn(
                          'absolute left-4 block lg:hidden ',
                          {
                            'top-2 ': selectedIndex === index,
                          },
                          {
                            'top-0 bottom-0 my-auto ': selectedIndex !== index,
                          }
                        )}
                        color={color}
                      />
                      {selectedIndex === index && (
                        <>
                          {!isCurrentProduct ? (
                            <Link href={`/product/${product.slug}`}>
                              <div className="label absolute bottom-6 right-1/2 h-12 translate-x-1/2 cursor-pointer  whitespace-nowrap rounded-sm bg-white/75 px-6 py-3 text-center hover:bg-white lg:bottom-8">
                                {`View ${product.name}`}
                              </div>
                            </Link>
                          ) : (
                            <div className="label absolute bottom-6 right-1/2 h-12 translate-x-1/2 select-none  whitespace-nowrap rounded-sm bg-white/75  px-6 py-3 text-center lg:bottom-8">
                              {`You're viewing ${product.name}`}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )
                }
              )}
          </div>
          <div className="flex justify-between pt-4">
            <div
              onClick={handleSelectLighter}
              className={cn('flex cursor-pointer items-center  gap-2 ', {
                'pointer-events-none': selectedIndex == 0,
              })}
            >
              <div className="hidden lg:block">
                <FancyArrowLeft />
              </div>
              <div className="block lg:hidden">
                <FancyArrowUp />
              </div>
              <div className="body-1">Lighter</div>
            </div>
            <div
              onClick={handleSelectDarker}
              className={cn('flex cursor-pointer items-center  gap-2 ', {
                'pointer-events-none':
                  selectedIndex == similarColourProductSlugs.length - 1,
              })}
            >
              <div className="body-1">Darker</div>
              <div className="hidden lg:block">
                <FancyArrowRight fill="#646464" />
              </div>
              <div className="block lg:hidden">
                <FancyArrowDown />
              </div>
            </div>
          </div>
        </div>
        <style jsx>{`
          .item {
            flex: 1;
            height: 100%;
            background-position: top 75% left 75%;
            background-size: cover;
            background-repeat: no-repeat;
            transition: flex 0.5s ease-in;
            position: relative;
            cursor: pointer;
          }
          .active {
            flex: 4.26;
          }
          @media (min-width: 1024px) {
            .active {
              flex: 7.6;
            }
          }
        `}</style>
      </div>
    </>
  )
}

export default SimilarColours
